import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Button,
  Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import LanguageIcon from '@mui/icons-material/Language';
import EventIcon from '@mui/icons-material/Event';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import PersonIcon from '@mui/icons-material/Person';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ROUTES } from '../../utils/constants';

interface FaqItem {
  question: string;
  answer: string;
  linkTo?: string;
  linkLabel?: string;
}

const faqItems: FaqItem[] = [
  {
    question: 'How do I register my child for an event?',
    answer:
      'Go to the Events page, find the event you are interested in, click View Details, then scroll down to the Registration section. Tick the checkbox next to your child\'s name and click "Register Now".',
    linkTo: ROUTES.EVENTS,
    linkLabel: 'Go to Events',
  },
  {
    question: 'Can I register multiple children for the same event?',
    answer:
      'Yes! On the event detail page, simply tick the checkboxes next to all the children you would like to register, then click Register Now.',
    linkTo: ROUTES.EVENTS,
    linkLabel: 'Browse Events',
  },
  {
    question: 'How do I cancel a registration?',
    answer:
      'Go to My Registrations, find the event, and click on it to view the details. You can cancel the registration from the event detail page, provided the registration deadline has not passed.',
    linkTo: ROUTES.MY_REGISTRATIONS,
    linkLabel: 'View My Registrations',
  },
  {
    question: 'How do I add or remove a family member?',
    answer:
      'Family members are managed through the Team Jak CRM system. Please contact Team Jak directly to add, remove, or update family member information. You can view your current family members on your Profile page.',
    linkTo: ROUTES.PROFILE,
    linkLabel: 'View Profile',
  },
  {
    question: 'Can I use the app on my phone?',
    answer:
      'Yes! The Team Jak App is a web application that works in any modern browser on your phone, tablet, or computer. You can also install it as a Progressive Web App (PWA) for a native app-like experience - look for the install instructions on the login page.',
  },
  {
    question: 'How do I get notified about new events?',
    answer:
      'Go to your Profile page and click the "Enable" button in the Push Notifications section. Allow notifications when prompted by your browser to receive timely updates about new events and important changes.',
    linkTo: ROUTES.PROFILE,
    linkLabel: 'Go to Profile',
  },
  {
    question: 'What does the photo permission setting do?',
    answer:
      'This setting controls whether Team Jak has your permission to use photographs of your family taken at events in promotional materials. You can change this at any time from your Profile page.',
    linkTo: ROUTES.PROFILE,
    linkLabel: 'Manage Permissions',
  },
  {
    question: 'What does the "Child Name & Birthday Display" permission do?',
    answer:
      "This setting controls whether Team Jak can display your child's name and birthday information. You can change this at any time from your Profile page.",
    linkTo: ROUTES.PROFILE,
    linkLabel: 'Manage Permissions',
  },
  {
    question: 'What does the "News & Event Updates" permission do?',
    answer:
      'This setting controls whether you consent to receive news and information about upcoming Team Jak events and activities. You can change this at any time from your Profile page.',
    linkTo: ROUTES.PROFILE,
    linkLabel: 'Manage Permissions',
  },
  {
    question: 'What does the "Online Activities & Screenshots" permission do?',
    answer:
      'This setting controls whether Team Jak can use screenshots or recordings from online activities involving your child. You can change this at any time from your Profile page.',
    linkTo: ROUTES.PROFILE,
    linkLabel: 'Manage Permissions',
  },
  {
    question: 'I am having trouble signing in. What should I try?',
    answer:
      'First, check that you are using the correct email address registered with Team Jak. If you are not receiving the sign-in code, please check your junk or spam folder. The email will come from Team Jak. If you continue to have problems, contact Team Jak for assistance.',
  },
];

const SupportPage: React.FC = () => {
  const navigate = useNavigate();

  const getIconForRoute = (route?: string) => {
    if (!route) return <ArrowForwardIcon />;
    if (route === ROUTES.EVENTS) return <EventIcon />;
    if (route === ROUTES.MY_REGISTRATIONS) return <BookmarkIcon />;
    if (route === ROUTES.PROFILE) return <PersonIcon />;
    return <ArrowForwardIcon />;
  };

  return (
    <Box>
      {/* Page Header */}
      <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
        Help & Support
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Find answers to common questions and get in touch with Team Jak
      </Typography>

      {/* FAQ Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 2,
          backgroundColor: '#f9f9f9',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <HelpOutlineIcon sx={{ color: '#00b7ed' }} />
          <Typography variant="h6" fontWeight={600}>
            Frequently Asked Questions
          </Typography>
        </Box>

        {faqItems.map((faq, index) => (
          <Accordion
            key={index}
            elevation={0}
            sx={{
              backgroundColor: 'white',
              mb: 1,
              borderRadius: '8px !important',
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                margin: '0 0 8px 0',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '&.Mui-expanded': {
                  minHeight: 48,
                },
                '& .MuiAccordionSummary-content.Mui-expanded': {
                  margin: '12px 0',
                },
              }}
            >
              <Typography fontWeight={500}>{faq.question}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: faq.linkTo ? 2 : 0 }}
              >
                {faq.answer}
              </Typography>
              {faq.linkTo && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={getIconForRoute(faq.linkTo)}
                  onClick={() => navigate(faq.linkTo!)}
                  sx={{
                    borderColor: '#00b7ed',
                    color: '#00b7ed',
                    '&:hover': {
                      borderColor: '#0095c4',
                      backgroundColor: 'rgba(0, 183, 237, 0.04)',
                    },
                  }}
                >
                  {faq.linkLabel}
                </Button>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      {/* Contact Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          backgroundColor: '#f9f9f9',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <ContactSupportIcon sx={{ color: '#00b7ed' }} />
          <Typography variant="h6" fontWeight={600}>
            Contact Team Jak
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          For account issues (updating email addresses, adding family members) or questions
          not covered above, please contact Team Jak directly.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LanguageIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            <Typography variant="body2">
              Website:{' '}
              <Link
                href="https://www.teamjak.org.uk"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#00b7ed' }}
              >
                www.teamjak.org.uk
              </Link>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LanguageIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            <Typography variant="body2">
              App:{' '}
              <Link
                href="https://app.teamjak.org.uk"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#00b7ed' }}
              >
                app.teamjak.org.uk
              </Link>
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default SupportPage;
